// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========

import { mergeOverlay } from '../mergeOverlay';
import ar_overlay from './hanggent/ar.json';
import de_overlay from './hanggent/de.json';
import en_overlay from './hanggent/en-us.json';
import es_overlay from './hanggent/es.json';
import fr_overlay from './hanggent/fr.json';
import it_overlay from './hanggent/it.json';
import ja_overlay from './hanggent/ja.json';
import ko_overlay from './hanggent/ko.json';
import ru_overlay from './hanggent/ru.json';
import zh_Hans_overlay from './hanggent/zh-Hans.json';
import zh_Hant_overlay from './hanggent/zh-Hant.json';
import ar from './ar';
import { appPageEn, appPageTranslations } from './appPages';
import de from './de';
import en from './en-us/index';
import es from './es/index';
import fr from './fr';
import it from './it';
import ja from './ja/index';
import ko from './ko';
import { realEstateTranslations } from './realEstate';
import ru from './ru';
import zh_Hans from './zh-Hans/index';
import zh_Hant from './zh-Hant/index';

import { brandResources } from '@/lib/brand';
import { stockAlertTranslations } from './hanggent/products/stockAlerts';
import {
  stockAnalysisTranslations,
  type StockAnalysisLocale,
} from './hanggent/products/stockAnalysis';
import { stockProductTranslations } from './hanggent/products/stockProducts';

/**
 * The product pages' own message modules, added under their own namespaces.
 * Added before the overlay, which only fills in what is still missing, so a
 * generated default never replaces a written translation.
 */
function withProducts<T>(locale: StockAnalysisLocale, messages: T): T {
  return mergeOverlay(messages, {
    realEstate: realEstateTranslations[locale],
    stockAnalysis: stockAnalysisTranslations[locale],
    stockAlerts: stockAlertTranslations[locale],
    stockProducts: stockProductTranslations[locale],
    ...(appPageTranslations[locale] ?? appPageEn),
  });
}

const unbranded = {
  'en-US': {
    translation: mergeOverlay(withProducts('en-US', en), en_overlay),
  },
  'zh-Hans': {
    translation: mergeOverlay(withProducts('zh-Hans', zh_Hans), zh_Hans_overlay),
  },
  'zh-Hant': {
    translation: mergeOverlay(withProducts('zh-Hant', zh_Hant), zh_Hant_overlay),
  },
  es: {
    translation: mergeOverlay(withProducts('es', es), es_overlay),
  },
  ja: {
    translation: mergeOverlay(withProducts('ja', ja), ja_overlay),
  },
  de: {
    translation: mergeOverlay(withProducts('de', de), de_overlay),
  },
  fr: {
    translation: mergeOverlay(withProducts('fr', fr), fr_overlay),
  },
  ru: {
    translation: mergeOverlay(withProducts('ru', ru), ru_overlay),
  },
  it: {
    translation: mergeOverlay(withProducts('it', it), it_overlay),
  },
  ar: {
    translation: mergeOverlay(withProducts('ar', ar), ar_overlay),
  },
  ko: {
    translation: mergeOverlay(withProducts('ko', ko), ko_overlay),
  },
};

export const resources = brandResources(unbranded);
